import { useEffect, useState } from "react";
import { Award, Bike, Clock, Lock, Route } from "lucide-react";
import { MyNewBadges } from "@/components/slots/Milestones";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-2xl border bg-card p-4">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><Icon className="h-4 w-4 text-primary" /> {label}</div>
    <div className="mt-2 font-display text-3xl font-extrabold">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Badges = ({ items, tone, testid }) => {
  const next = items.find((b) => !b.achieved);
  return (
    <div data-testid={testid} className="flex flex-wrap gap-2">
      {items.map((b) => (
        <span key={b.value} data-testid={`${testid}-${b.value}`} data-achieved={b.achieved}
          className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-bold ${b.achieved ? tone : "border-dashed text-muted-foreground opacity-60"}`}>
          {b.achieved ? <Award className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />} {b.label}
        </span>
      ))}
      {next && <span className="self-center text-xs text-muted-foreground">Next: {next.label}</span>}
    </div>
  );
};

export const RiderLogbook = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/my/logbook").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <section data-testid="rider-logbook" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-bold">My logbook</h2>
        {d.first_run && <span className="text-xs text-muted-foreground">Riding since {fmtDate(d.first_run)}{d.favourite_bike ? ` · most ridden: ${d.favourite_bike}` : ""}</span>}
      </div>
      <MyNewBadges labels={d.recent_badges} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Bike} label="Runs" value={d.runs.toLocaleString("en-GB")} sub={`${d.runs_12m} in the last 12 months`} testid="logbook-runs" />
        <Stat icon={Route} label="Miles" value={d.miles.toLocaleString("en-GB")} sub={`${d.miles_12m.toLocaleString("en-GB")} in the last 12 months`} testid="logbook-miles" />
        <Stat icon={Clock} label="Hours on runs" value={d.hours} testid="logbook-hours" />
        <Stat icon={Award} label="Badges" value={[...d.run_badges, ...d.mile_badges].filter((b) => b.achieved).length} sub={`of ${d.run_badges.length + d.mile_badges.length}`} testid="logbook-badge-count" />
      </div>
      <div className="space-y-3 rounded-2xl border bg-card p-4">
        <Badges items={d.run_badges} tone="border-amber-400 bg-amber-400/15 text-amber-300" testid="run-badges" />
        <Badges items={d.mile_badges} tone="border-sky-400 bg-sky-400/15 text-sky-300" testid="mile-badges" />
      </div>
      {d.runs === 0 && <p data-testid="logbook-empty" className="text-sm text-muted-foreground">Complete a pre-ride and post-ride check on a run to start your logbook.</p>}
    </section>
  );
};
